/**
 * Google Consent Mode v2 vocabulary.
 *
 * This is Google's language, not ours. The Consent Foundation
 * (`lib/consent`) owns what the VISITOR agreed to — `essential` /
 * `analytics` / `marketing`, the three categories the cookie banner asks
 * about. This file owns how that answer is spelled to Google, and
 * `consentMode.ts` owns the translation between the two. Keeping the two
 * vocabularies apart is deliberate: Google has already renamed and extended
 * these signals once (v2 added `ad_user_data` and `ad_personalization` in
 * 2024), and when it happens again the change lands here rather than in the
 * banner or the stored cookie.
 *
 * TypeScript rendition of the shared Consent Mode module. The canonical source
 * is landing-page/src/lib/consent-mode/ (JavaScript); this copy must stay in
 * step with it, and with invite/src/lib/consent-mode/ (vendored byte-identical).
 * Keep the copies in sync with `npm run verify:consent` — see
 * docs/analytics/google-consent-mode.md.
 */

/**
 * The six Consent Mode v2 signals we manage. Google defines others
 * (`ads_data_redaction`, `url_passthrough`); those are deliberately NOT set
 * here — see "Future extension points" in the docs.
 */
export const GoogleConsentSignals = Object.freeze({
  AD_STORAGE: 'ad_storage',
  AD_USER_DATA: 'ad_user_data',
  AD_PERSONALIZATION: 'ad_personalization',
  ANALYTICS_STORAGE: 'analytics_storage',
  FUNCTIONALITY_STORAGE: 'functionality_storage',
  SECURITY_STORAGE: 'security_storage',
} as const);

/** The only two values a Consent Mode signal may take. */
export const ConsentSignalState = Object.freeze({
  GRANTED: 'granted',
  DENIED: 'denied',
} as const);

/**
 * The default consent state, established BEFORE the GTM container loads.
 *
 * IMPORTANT: this object is a mirror, not the source. The values that actually
 * reach Google first are hard-coded in the inline `<script>` at the top of each
 * index.html — they must run before GTM, which is long before any bundled
 * module executes, so they cannot be imported from here. `npm run verify:consent`
 * asserts the inline snippets and this map agree; if you change one, change all
 * four (three index.html files + this constant) or the check fails.
 *
 * Everything measurable is denied. `functionality_storage` and
 * `security_storage` are granted because they cover strictly necessary storage,
 * which under Article 4(5) of Greek Law 3471/2006 does not require consent —
 * the same reasoning that makes `essential` always true in the foundation.
 */
export const DEFAULT_CONSENT_MODE_SIGNALS = Object.freeze({
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
} as const);

/**
 * How long (ms) Google should hold tags waiting for an `update` before acting on
 * the default. Our update arrives from a bundled module, i.e. after HTML parse,
 * so without this a returning visitor who accepted could have their first
 * pageview measured in cookieless-ping mode. 500ms is Google's recommended
 * value for exactly this asynchronous-CMP shape.
 *
 * Note the failure direction: if the bundle is slower than the wait, Google
 * proceeds with the DEFAULT, which is denied. Late means under-measured, never
 * over-collected.
 */
export const CONSENT_MODE_WAIT_FOR_UPDATE_MS = 500;

/** The gtag command name and its two sub-commands. */
export const GTAG_CONSENT_COMMAND = 'consent';
export const GTAG_CONSENT_DEFAULT = 'default';
export const GTAG_CONSENT_UPDATE = 'update';

/**
 * The dataLayer event announcing that the consent signals just changed.
 *
 * This is OUR vocabulary — the one thing in this file that is not Google's. It
 * exists because the two readers of a consent change read the dataLayer very
 * differently. Google's tags read the `gtag('consent', 'update', …)` command
 * directly. A GTM TRIGGER cannot: GTM exposes no consent state to trigger
 * conditions, so a container has no way to react at the MOMENT consent changes.
 * A tag blocked by a denied signal when the page loaded therefore stays blocked
 * after the visitor accepts, until the next page load. This event is the trigger
 * GTM otherwise lacks.
 *
 * It is a published contract: GTM triggers key off this exact string, so it must
 * never change once live — the same rule as the analytics event names, and the
 * reason it is a constant here rather than a literal at the call site.
 *
 * Deliberately NOT in `AnalyticsEvents` (`lib/analytics/constants.ts`). That
 * catalog is the marketing/product vocabulary: every event there describes
 * something a VISITOR did and carries `page`/`language` enrichment. This
 * describes what WE told Google, is emitted by this module, and belongs to the
 * consent contract that `npm run verify:consent` guards.
 */
export const CONSENT_UPDATED_EVENT = 'consent_updated';

/**
 * The payload key the six signals are carried under in that event.
 *
 * Nested rather than flattened, so GTM reads them as `consent.ad_storage`: one
 * namespaced object that cannot collide with an analytics parameter, and that
 * reads in Tag Assistant exactly like the signal set it mirrors.
 */
export const CONSENT_SIGNALS_KEY = 'consent';

/** A Consent Mode signal name. */
export type GoogleConsentSignal = (typeof GoogleConsentSignals)[keyof typeof GoogleConsentSignals];

/** A Consent Mode signal value. */
export type ConsentSignalValue = (typeof ConsentSignalState)[keyof typeof ConsentSignalState];
